import React from 'react';

/**
 * Примитивы дизайн-системы v2 (AIT-378). Стили в styles/ds/primitives.css.
 * Это тонкие обёртки над классами ds-*: страницы могут использовать и классы
 * напрямую, компоненты нужны ради типизации вариантов и единых дефолтов.
 */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'md' | 'sm';
  icon?: string;
  loading?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'secondary',
  size = 'md',
  icon,
  loading = false,
  className = '',
  children,
  type = 'button',
  disabled,
  ...rest
}) => (
  <button
    type={type === 'submit' ? 'submit' : 'button'}
    className={[
      'ds-btn',
      `ds-btn--${variant}`,
      size === 'sm' ? 'ds-btn--sm' : '',
      !children ? 'ds-btn--icon' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ')}
    disabled={disabled || loading}
    {...rest}
  >
    {loading ? (
      <span className="ds-spinner" aria-hidden="true" />
    ) : icon ? (
      <i className={`fas ${icon}`} aria-hidden="true" />
    ) : null}
    {children}
  </button>
);

export interface TabItem<K extends string = string> {
  key: K;
  label: React.ReactNode;
  count?: number | string;
  icon?: string;
  id?: string;
}

export interface TabsProps<K extends string = string> {
  items: TabItem<K>[];
  value: K;
  onChange: (key: K) => void;
  className?: string;
  'aria-label'?: string;
}

export function Tabs<K extends string = string>({ items, value, onChange, className = '', ...rest }: TabsProps<K>) {
  return (
    <div className={['ds-tabs', className].filter(Boolean).join(' ')} role="tablist" aria-label={rest['aria-label']}>
      {items.map((t) => (
        <button
          key={t.key}
          id={t.id}
          type="button"
          role="tab"
          aria-selected={t.key === value}
          className="ds-tab"
          onClick={() => onChange(t.key)}
        >
          {t.icon ? <i className={`fas ${t.icon}`} aria-hidden="true" /> : null}
          {t.label}
          {t.count !== undefined && t.count !== null && t.count !== '' ? (
            <span className="ds-tab__count">{t.count}</span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

export type BadgeTone = 'neutral' | 'ok' | 'warn' | 'bad' | 'info';

export const Badge: React.FC<{
  tone?: BadgeTone;
  dot?: boolean;
  className?: string;
  children: React.ReactNode;
  title?: string;
}> = ({ tone = 'neutral', dot = true, className = '', children, title }) => (
  <span
    className={['ds-badge', tone !== 'neutral' ? `ds-badge--${tone}` : '', dot ? '' : 'ds-badge--plain', className]
      .filter(Boolean)
      .join(' ')}
    title={title}
  >
    {children}
  </span>
);

export const Spinner: React.FC<{ size?: 'md' | 'lg'; label?: string }> = ({ size = 'md', label }) =>
  label ? (
    <div className="ds-loading" role="status">
      <span className={`ds-spinner${size === 'lg' ? ' ds-spinner--lg' : ''}`} aria-hidden="true" />
      {label}
    </div>
  ) : (
    <span className={`ds-spinner${size === 'lg' ? ' ds-spinner--lg' : ''}`} role="status" aria-label="Loading" />
  );

export const PageHeader: React.FC<{
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  meta?: React.ReactNode;
  actions?: React.ReactNode;
  as?: 'h1' | 'h2';
  className?: string;
}> = ({ title, subtitle, meta, actions, as = 'h1', className = '' }) => {
  const Tag = as;
  return (
    <div className={['ds-page-head', className].filter(Boolean).join(' ')}>
      <div>
        {meta ? <div className="ds-page-head__meta">{meta}</div> : null}
        <Tag>{title}</Tag>
        {subtitle ? <p className="ds-page-head__sub">{subtitle}</p> : null}
      </div>
      {actions ? <div className="ds-page-head__actions">{actions}</div> : null}
    </div>
  );
};

export const SectionHeader: React.FC<{ title: React.ReactNode; hint?: React.ReactNode; actions?: React.ReactNode }> = ({
  title,
  hint,
  actions,
}) => (
  <div className="ds-section__head">
    <h3>{title}</h3>
    {hint ? <p>{hint}</p> : null}
    {actions ? <div className="ds-section__actions">{actions}</div> : null}
  </div>
);

export interface StatItem {
  label: React.ReactNode;
  value: React.ReactNode;
  detail?: React.ReactNode;
  tone?: 'up' | 'down';
}

export const StatRow: React.FC<{ items: StatItem[]; className?: string }> = ({ items, className = '' }) => (
  <div className={['ds-stats', className].filter(Boolean).join(' ')}>
    {items.map((s, i) => (
      <div className="ds-stat" key={i}>
        <div className="ds-stat__k">{s.label}</div>
        <div className="ds-stat__v">{s.value}</div>
        {s.detail !== undefined ? (
          <div className={['ds-stat__d', s.tone ? `ds-stat__d--${s.tone}` : ''].filter(Boolean).join(' ')}>
            {s.detail}
          </div>
        ) : null}
      </div>
    ))}
  </div>
);

export const EmptyState: React.FC<{
  title: React.ReactNode;
  text?: React.ReactNode;
  action?: React.ReactNode;
  icon?: string;
}> = ({ title, text, action, icon }) => (
  <div className="ds-empty">
    {icon ? (
      <span className="ds-empty__icon" aria-hidden="true">
        <i className={`fas ${icon}`} />
      </span>
    ) : null}
    <h4>{title}</h4>
    {text ? <p>{text}</p> : null}
    {action}
  </div>
);

export const Alert: React.FC<{
  tone?: 'info' | 'error' | 'success' | 'warn';
  onClose?: () => void;
  children: React.ReactNode;
  role?: string;
}> = ({ tone = 'info', onClose, children, role }) => (
  <div
    className={['ds-alert', tone !== 'info' ? `ds-alert--${tone}` : ''].filter(Boolean).join(' ')}
    role={role || (tone === 'error' ? 'alert' : 'status')}
  >
    <div>{children}</div>
    {onClose ? (
      <button type="button" className="ds-alert__close" onClick={onClose} aria-label="Dismiss">
        <i className="fas fa-times" aria-hidden="true" />
      </button>
    ) : null}
  </div>
);

export const Avatar: React.FC<{ name?: string; email?: string; className?: string }> = ({
  name,
  email,
  className = '',
}) => {
  const src = (name && name.trim()) || (email && email.split('@')[0]) || '';
  const parts = src.split(/[\s._-]+/).filter(Boolean);
  const text = parts.length >= 2 ? parts[0][0] + parts[1][0] : src.slice(0, 2) || '?';
  return <span className={['ds-avatar', className].filter(Boolean).join(' ')}>{text}</span>;
};
